import { getApp, getApps, initializeApp } from 'firebase/app';
import type { Auth, Unsubscribe, User, UserCredential } from 'firebase/auth';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'koob-polymer.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
export const storage = getStorage(app);

let authModulePromise: Promise<typeof import('firebase/auth')> | null = null;
let authInstancePromise: Promise<Auth> | null = null;

async function loadAuthModule() {
  authModulePromise ??= import('firebase/auth');
  return authModulePromise;
}

// Lazy-load auth so the public marketing route is not blocked by Firebase Auth.
export async function getAuthInstance(): Promise<Auth> {
  authInstancePromise ??= loadAuthModule().then(({ getAuth }) => getAuth(app));
  return authInstancePromise;
}

export async function subscribeToAuthChanges(
  callback: (user: User | null) => void,
): Promise<Unsubscribe> {
  const [auth, { onAuthStateChanged }] = await Promise.all([
    getAuthInstance(),
    loadAuthModule(),
  ]);

  return onAuthStateChanged(auth, callback);
}

export async function signInWithEmailPassword(
  email: string,
  password: string,
): Promise<UserCredential> {
  const [auth, { signInWithEmailAndPassword }] = await Promise.all([
    getAuthInstance(),
    loadAuthModule(),
  ]);

  return signInWithEmailAndPassword(auth, email, password);
}

export async function signInWithCustomTokenValue(
  token: string,
): Promise<UserCredential> {
  const [auth, { signInWithCustomToken }] = await Promise.all([
    getAuthInstance(),
    loadAuthModule(),
  ]);

  return signInWithCustomToken(auth, token);
}

export async function fetchSignInMethodsForEmailAddress(
  email: string,
): Promise<string[]> {
  const [auth, { fetchSignInMethodsForEmail }] = await Promise.all([
    getAuthInstance(),
    loadAuthModule(),
  ]);

  return fetchSignInMethodsForEmail(auth, email);
}

export async function createEmailPasswordUser(
  email: string,
  password: string,
): Promise<UserCredential> {
  const [auth, { createUserWithEmailAndPassword }] = await Promise.all([
    getAuthInstance(),
    loadAuthModule(),
  ]);

  return createUserWithEmailAndPassword(auth, email, password);
}

export async function sendPasswordResetEmailAddress(
  email: string,
): Promise<void> {
  const [auth, { sendPasswordResetEmail }] = await Promise.all([
    getAuthInstance(),
    loadAuthModule(),
  ]);

  return sendPasswordResetEmail(auth, email);
}

async function createGoogleProvider() {
  const authModule = await loadAuthModule();
  const provider = new authModule.GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });

  return { authModule, provider };
}

export async function signInWithGooglePopup(): Promise<UserCredential> {
  const [auth, { authModule, provider }] = await Promise.all([
    getAuthInstance(),
    createGoogleProvider(),
  ]);

  return authModule.signInWithPopup(auth, provider);
}

export async function signInWithGoogleRedirect(): Promise<void> {
  const [auth, { authModule, provider }] = await Promise.all([
    getAuthInstance(),
    createGoogleProvider(),
  ]);

  return authModule.signInWithRedirect(auth, provider);
}

export async function getGoogleRedirectResult(): Promise<UserCredential | null> {
  const [auth, { getRedirectResult }] = await Promise.all([
    getAuthInstance(),
    loadAuthModule(),
  ]);

  return getRedirectResult(auth);
}

/**
 * Whether this sign-in created its Firebase account (Firebase's isNewUser).
 * A credential without sign-in metadata counts as new, so callers that relax a
 * check for existing accounts stay strict when in doubt.
 */
export async function isNewUserCredential(credential: UserCredential): Promise<boolean> {
  const { getAdditionalUserInfo } = await loadAuthModule();

  return getAdditionalUserInfo(credential)?.isNewUser ?? true;
}

export async function signOutUser(): Promise<void> {
  const [auth, { signOut }] = await Promise.all([
    getAuthInstance(),
    loadAuthModule(),
  ]);

  await signOut(auth);
}
